import type { ElementCreator } from '../element.model';
import type { FeatureCreator } from './feature.model';
import { AmplitudeCreator } from './amplitude/amplitude.creator';
import { AsymptotesCreator } from './asymptotes/asymptotes.creator';
import { AxisOfSymmetryCreator } from './axis-of-symmetry/axis-of-symmetry.creator';
import { DerivativeCreator } from './derivative/derivative.creator';
import { DomainCreator } from './domain/domain.creator';
import { ExtremaCreator } from './extrema/extrema.creator';
import { FunctionAreaCreator } from './function-area/function-area.creator';
import { HandleCreator, type HandleDragCallback } from './handle/handle.creator';
import { InflectionCreator } from './inflection/inflection.creator';
import { IntersectionCreator } from './intersection/intersection.creator';
import { IntersectionAreaCreator } from './intersection-area/intersection-area.creator';
import { MonotonicityCreator } from './monotonicity/monotonicity.creator';
import { PeriodCreator } from './period/period.creator';
import { RangeCreator } from './range/range.creator';
import { PhaseShiftCreator } from './phase-shift/phase-shift.creator';
import { RootsCreator } from './roots/roots.creator';
import { SecantCreator } from './secant/secant.creator';
import { TangentCreator } from './tangent/tangent.creator';
import { TrajectoryCreator } from './trajectory/trajectory.creator';
import { WavelengthCreator } from './wavelength/wavelength.creator';
import { YInterceptCreator } from './y-intercept/y-intercept.creator';

/**
 * Registry of all feature creators.
 * Add new feature types here as they are implemented.
 */
export class FeatureRegistry {
  private static _creators: FeatureCreator[] | null = null;
  private static _handleCreator: HandleCreator | null = null;

  private static get creators(): FeatureCreator[] {
    if (!this._creators) {
      this._creators = [
        new AmplitudeCreator(),
        new AsymptotesCreator(),
        new AxisOfSymmetryCreator(),
        new DerivativeCreator(),
        new DomainCreator(),
        new ExtremaCreator(),
        new FunctionAreaCreator(),
        this.handleCreator,
        new InflectionCreator(),
        new IntersectionCreator(),
        new IntersectionAreaCreator(),
        new MonotonicityCreator(),
        new PeriodCreator(),
        new PhaseShiftCreator(),
        new RangeCreator(),
        new RootsCreator(),
        new SecantCreator(),
        new TangentCreator(),
        new TrajectoryCreator(),
        new WavelengthCreator(),
        new YInterceptCreator(),
      ];
    }
    return this._creators;
  }

  /**
   * Get the handle creator instance (singleton for callback registration).
   */
  static get handleCreator(): HandleCreator {
    if (!this._handleCreator) {
      this._handleCreator = new HandleCreator();
    }
    return this._handleCreator;
  }

  /**
   * Set the callback for handle drag events.
   */
  static setHandleDragCallback(callback: HandleDragCallback): void {
    this.handleCreator.setOnDragCallback(callback);
  }

  /**
   * Get all registered feature creators as ElementCreators.
   */
  static getAll(): ElementCreator[] {
    return this.creators;
  }

  /**
   * Get a specific feature creator by type.
   */
  static get(type: string): FeatureCreator | undefined {
    return this.creators.find((c) => c.featureType === type);
  }

  /**
   * Get all registered feature types.
   */
  static getTypes(): string[] {
    return this.creators.map((c) => c.featureType);
  }

  /**
   * Get the computed value keys for a feature type.
   */
  static getComputedValueKeys(type: string): readonly string[] {
    return this.get(type)?.computedValueKeys ?? [];
  }

  /**
   * Get the computed collection keys for a feature type.
   */
  static getComputedCollectionKeys(type: string): ReadonlyArray<{ name: string; properties: readonly string[] }> {
    return this.get(type)?.computedCollectionKeys ?? [];
  }
}
